import { styled } from "@pigment-css/react";
import { CrossLarge, SkipForward } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import type { Customer, Meeting } from "@acto/taskpane/core/domain";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { useMorphTransition } from "./useMorphTransition";
import { useVisualViewportHeight } from "./useVisualViewportHeight";
import { useRecapFlow } from "./useRecapFlow";
import { RecapAnimationScreen } from "./RecapAnimationScreen";
import { RecapSummaryScreen } from "./RecapSummaryScreen";
import { TitleBar } from "./TitleBar";
import { CardStack } from "./CardStack";
import { WritingField } from "./WritingField";
import { FlowFooter } from "./FlowFooter";
import { MorphFlyer } from "./MorphFlyer";

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
  // Animates only in writing mode, where an explicit height is set inline so the
  // footer rides up above the OS keyboard. Harmless when height is unset.
  transition: "height 220ms ease",
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

interface RecapCreationFlowProps {
  /** Meetings to recap. Empty (with `adHocCustomer` set) runs the ad-hoc flow. */
  meetings?: Meeting[];
  /** Customer for an ad-hoc ("Neue Nachbereitung") recap — used only when `meetings` is empty. */
  adHocCustomer?: Customer;
  /** Visit date for the ad-hoc meeting (e.g. from mark-as-done). Defaults to today. */
  adHocSeed?: { scheduledDate?: string };
  onClose: () => void;
}

export function RecapCreationFlow({ meetings = [], adHocCustomer, adHocSeed, onClose }: RecapCreationFlowProps) {
  const t = useTranslations("fieldSalesTwo.recaps");

  const {
    writing,
    flightHidden,
    cardRef,
    pillRef,
    inputRef,
    flyerRef,
    cardLayerRef,
    pillLayerRef,
    enterWriting,
    exitWriting,
  } = useMorphTransition();

  const viewportHeight = useVisualViewportHeight();

  const {
    recap,
    items,
    safeIndex,
    meeting,
    isMulti,
    isAdHoc,
    name,
    expanded,
    setExpanded,
    screen,
    transcript,
    setTranscript,
    summaryNote,
    summaryText,
    recording,
    elapsedSeconds,
    startRecording,
    stopRecording,
    handleEnterWriting,
    cycle,
    skip,
    handleConfirm,
    editRecapContent,
    pendingEditContent,
    discardPendingEdit,
    handleNext,
  } = useRecapFlow({
    meetings,
    adHocCustomer,
    adHocSeed,
    onClose,
    onEnterWriting: enterWriting,
    onExitWriting: exitWriting,
  });

  // Leaving the writing screen (back or close) must also tear down an in-flight recording.
  const handleBack = () => {
    stopRecording();
    exitWriting();
  };
  const handleClose = () => {
    stopRecording();
    onClose();
  };

  if (!meeting) return null;

  // Badge shape must stay uniform while cycling the batch. Derived from the stable
  // `meetings` prop (not the shrinking `items`) so it can't flip mid-flow.
  const showBadgeTime = meetings.every((m) => m.scheduledStartTime);

  if (screen === "animating") {
    return <RecapAnimationScreen customerName={name} isSummarizing={recap.isSummarizing} onClose={onClose} />;
  }

  if (screen === "summary" && summaryNote) {
    return (
      <RecapSummaryScreen
        note={summaryNote}
        meeting={meeting}
        hideDate={isAdHoc}
        showBadgeTime={showBadgeTime}
        summaryText={summaryText}
        onEditRecapContent={editRecapContent}
        pendingEditContent={pendingEditContent}
        onDiscardPendingEdit={discardPendingEdit}
        onContinue={handleNext}
        onClose={onClose}
      />
    );
  }

  // Single mode has no skip button, so an invisible one counterweighs the leading close
  // button. Without it the TopNav centre track (and the name pill in it) sits off-centre.
  const skipNode = isMulti ? (
    <HeaderIconButton type="button" aria-label={t("a11y.skip")} onClick={skip}>
      <SkipForward width={18} height={14} />
    </HeaderIconButton>
  ) : (
    <HeaderIconButton type="button" tabIndex={-1} aria-hidden style={{ visibility: "hidden" }} />
  );

  return (
    // Writing mode pins the page to the visible viewport so the input + footer fill
    // it and animate up when the keyboard shows. Other modes keep natural flow + scroll.
    <Page style={writing ? { height: viewportHeight } : undefined}>
      <TopNav
        leading={
          <HeaderIconButton
            type="button"
            aria-label={writing ? t("a11y.back") : t("a11y.close")}
            onClick={writing ? handleBack : handleClose}
          >
            <CrossLarge width={18} height={18} />
          </HeaderIconButton>
        }
        title={
          <TitleBar
            isMulti={isMulti}
            writing={writing}
            name={name}
            itemCount={items.length}
            pillRef={pillRef}
            flightHidden={flightHidden}
            onPrev={() => cycle(-1)}
            onNext={() => cycle(1)}
            onExitWriting={exitWriting}
          />
        }
        trailing={skipNode}
      />

      <Body>
        {!writing && (
          <CardStack
            items={items}
            safeIndex={safeIndex}
            meeting={meeting}
            hideDate={isAdHoc}
            showBadgeTime={showBadgeTime}
            expanded={expanded}
            onToggleExpand={() => setExpanded((v) => !v)}
            cardRef={cardRef}
            flightHidden={flightHidden}
          />
        )}

        <WritingField
          writing={writing}
          transcript={transcript}
          readOnly={recording}
          recording={recording}
          onChangeTranscript={setTranscript}
          onEnterWriting={handleEnterWriting}
          inputRef={inputRef}
        />
      </Body>

      <FlowFooter
        writing={writing}
        recording={recording}
        elapsedSeconds={elapsedSeconds}
        confirmDisabled={recording || transcript.trim().length === 0}
        onConfirm={handleConfirm}
        onEnterWriting={handleEnterWriting}
        onStartRecording={startRecording}
        onStop={stopRecording}
      />

      <MorphFlyer
        flyerRef={flyerRef}
        cardLayerRef={cardLayerRef}
        pillLayerRef={pillLayerRef}
        meeting={meeting}
        hideDate={isAdHoc}
        showBadgeTime={showBadgeTime}
        name={name}
      />
    </Page>
  );
}
